import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Abattoir, AbattoirInspection, AbattoirThroughputLog, LivestockHolding, Page, VetBooking } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const SPECIES = ['CATTLE', 'PIG', 'GOAT', 'SHEEP', 'POULTRY_LAYER', 'POULTRY_BROILER', 'DUCK', 'BEE_COLONY'] as const;

type Tab = 'abattoirs' | 'vet';

/** M33 Agriculture — abattoir facility compliance (throughput, hygiene inspections) and veterinary clinic/farm-visit bookings. */
export function AbattoirVet() {
  const [tab, setTab] = useState<Tab>('abattoirs');
  return (
    <div>
      <PageHeader title="Abattoirs & Vet Bookings" subtitle="Abattoir throughput/hygiene inspections, and vet clinic/farm-visit scheduling (M33)." />
      <TabStrip tabs={[{ key: 'abattoirs', label: 'Abattoirs' }, { key: 'vet', label: 'Vet Bookings' }]} active={tab} onChange={setTab} />
      {tab === 'abattoirs' ? <AbattoirsTab /> : <VetTab />}
    </div>
  );
}

// ── Abattoirs ────────────────────────────────────────────────────────────

function AbattoirsTab() {
  const { persona } = useAuth();
  const isAgriOfficer = persona ? persona.roles.includes('AGRI_OFFICER') : false;
  const isVet = persona ? persona.roles.includes('VET') : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m33/abattoirs?limit=50${status ? `&status=${status}` : ''}`, [status]);
  const listQ = useApiQuery<Page<Abattoir>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['ACTIVE', 'SUSPENDED', 'CLOSED'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {isAgriOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Register abattoir
          </button>
        ) : null}
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No abattoirs registered" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Facility No</th>
                <th className="table-head">Name</th>
                <th className="table-head">District</th>
                <th className="table-head">Licence status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{a.facilityNo}</td>
                  <td className="table-cell">{a.name}</td>
                  <td className="table-cell text-xs text-slate-500">{a.district}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.licenceStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <AbattoirDetailModal id={selectedId} isAgriOfficer={isAgriOfficer} isVet={isVet} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <NewAbattoirModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AbattoirDetailModal({
  id,
  isAgriOfficer,
  isVet,
  onClose,
  onChanged,
}: {
  id: string;
  isAgriOfficer: boolean;
  isVet: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const aq = useApiQuery<Abattoir>(`/api/v1/m33/abattoirs/${id}`);
  const throughputQ = useApiQuery<{ items: AbattoirThroughputLog[] }>(`/api/v1/m33/abattoirs/${id}/throughput`);
  const inspectionsQ = useApiQuery<{ items: AbattoirInspection[] }>(`/api/v1/m33/abattoirs/${id}/inspections`);
  const a = aq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showThroughput, setShowThroughput] = useState(false);
  const [logDate, setLogDate] = useState(new Date().toISOString().slice(0, 10));
  const [species, setSpecies] = useState<(typeof SPECIES)[number]>('PIG');
  const [headcount, setHeadcount] = useState('');
  const [showInspection, setShowInspection] = useState(false);
  const [passed, setPassed] = useState(true);
  const [notes, setNotes] = useState('');
  const [showClose, setShowClose] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  const refresh = () => {
    aq.reload();
    throughputQ.reload();
    inspectionsQ.reload();
    onChanged();
  };
  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const logThroughput = () =>
    call(() => api.post(`/api/v1/m33/abattoirs/${id}/throughput`, { logDate, species, headcount: Number.parseInt(headcount, 10) })).then(() => {
      setShowThroughput(false);
      setHeadcount('');
    });
  const recordInspection = () =>
    call(() => api.post(`/api/v1/m33/abattoirs/${id}/inspections`, { inspectedAt: new Date().toISOString(), passed, notes: notes || undefined })).then(() => {
      setShowInspection(false);
      setNotes('');
    });
  const reinstate = () => call(() => api.post(`/api/v1/m33/abattoirs/${id}/reinstate`));
  const close = () => call(() => api.post(`/api/v1/m33/abattoirs/${id}/close`, { reason: closeReason })).then(() => setShowClose(false));

  return (
    <Modal title={a ? `${a.facilityNo} — ${a.name}` : 'Abattoir'} onClose={onClose} wide>
      {aq.error ? <ErrorNote message={aq.error} /> : null}
      {aq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {a ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Licence status" value={<StatusBadge status={a.licenceStatus} />} />
            <Detail label="District" value={a.district} />
            <Detail label="Operator" value={`${a.operatorType}:${a.operatorId}`} mono />
          </div>
          {a.suspendedReason ? <ErrorNote message={`Suspended: ${a.suspendedReason}`} /> : null}
          {a.closedReason ? <Detail label="Closed reason" value={a.closedReason} /> : null}

          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="field-label !mb-0">Throughput log</div>
                {isAgriOfficer || isVet ? (
                  <button className="text-xs font-semibold text-brand-700 underline" onClick={() => setShowThroughput((s) => !s)}>
                    + Log
                  </button>
                ) : null}
              </div>
              {showThroughput ? (
                <div className="mb-2 space-y-2 rounded-lg border border-slate-200 p-2">
                  <input className="field-input" type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
                  <select className="field-input" value={species} onChange={(e) => setSpecies(e.target.value as (typeof SPECIES)[number])}>
                    {SPECIES.map((s) => (
                      <option key={s} value={s}>
                        {s.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <input className="field-input" type="number" placeholder="Headcount" value={headcount} onChange={(e) => setHeadcount(e.target.value)} />
                  <button className="btn-primary w-full" disabled={busy || !headcount} onClick={logThroughput}>
                    Submit
                  </button>
                </div>
              ) : null}
              {throughputQ.loading ? (
                <Spinner />
              ) : (throughputQ.data?.items.length ?? 0) === 0 ? (
                <p className="text-xs text-slate-400">No throughput logged.</p>
              ) : (
                <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                  {throughputQ.data!.items.map((l) => (
                    <li key={l.id} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{formatDate(l.logDate)}</span>
                      <span>
                        {l.species.replaceAll('_', ' ')}: {l.headcount}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="field-label !mb-0">Hygiene inspections</div>
                {isVet ? (
                  <button className="text-xs font-semibold text-brand-700 underline" onClick={() => setShowInspection((s) => !s)}>
                    + Record
                  </button>
                ) : null}
              </div>
              {showInspection ? (
                <div className="mb-2 space-y-2 rounded-lg border border-slate-200 p-2">
                  <select className="field-input" value={passed ? 'pass' : 'fail'} onChange={(e) => setPassed(e.target.value === 'pass')}>
                    <option value="pass">Passed</option>
                    <option value="fail">Failed (critical violations)</option>
                  </select>
                  <textarea className="field-input" rows={2} placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <button className="btn-primary w-full" disabled={busy} onClick={recordInspection}>
                    Submit
                  </button>
                </div>
              ) : null}
              {inspectionsQ.loading ? (
                <Spinner />
              ) : (inspectionsQ.data?.items.length ?? 0) === 0 ? (
                <p className="text-xs text-slate-400">No inspections recorded.</p>
              ) : (
                <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                  {inspectionsQ.data!.items.map((i) => (
                    <li key={i.id} className="flex justify-between border-b border-slate-100 py-1">
                      <span>{formatDateTime(i.inspectedAt)}</span>
                      <StatusBadge status={i.passed ? 'PASS' : 'FAIL'} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {isAgriOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showClose ? (
                <div className="flex gap-2">
                  {a.licenceStatus === 'SUSPENDED' ? (
                    <button className="btn-success" disabled={busy} onClick={reinstate}>
                      Reinstate
                    </button>
                  ) : null}
                  {a.licenceStatus !== 'CLOSED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowClose(true)}>
                      Close facility
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Reason">
                    <textarea className="field-input" rows={2} value={closeReason} onChange={(e) => setCloseReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowClose(false)}>
                      Cancel
                    </button>
                    <button className="btn-danger" disabled={busy || closeReason.trim().length < 3} onClick={close}>
                      Confirm close
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewAbattoirModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [district, setDistrict] = useState('');
  const [operatorType, setOperatorType] = useState<'PERSON' | 'ENTITY'>('ENTITY');
  const [operatorId, setOperatorId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/abattoirs', { name, district, operatorType, operatorId });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register abattoir facility" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Facility name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="District">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Operator type">
            <select className="field-input" value={operatorType} onChange={(e) => setOperatorType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="ENTITY">Entity</option>
              <option value="PERSON">Person</option>
            </select>
          </Field>
          <Field label={operatorType === 'ENTITY' ? 'Business ID' : 'NIN'}>
            <input className="field-input" value={operatorId} onChange={(e) => setOperatorId(e.target.value)} />
          </Field>
        </div>
        <button className="btn-primary w-full" disabled={busy || !name || !district || !operatorId} onClick={submit}>
          {busy ? 'Registering…' : 'Register facility'}
        </button>
      </div>
    </Modal>
  );
}

// ── Vet bookings ─────────────────────────────────────────────────────────

function VetTab() {
  const { persona } = useAuth();
  const isVet = persona ? persona.roles.includes('VET') : false;
  const isAgriOfficer = persona ? persona.roles.includes('AGRI_OFFICER') : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const path = useMemo(() => `/api/v1/m33/vet-bookings?limit=50${status ? `&status=${status}` : ''}`, [status]);
  const listQ = useApiQuery<Page<VetBooking>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['BOOKED', 'COMPLETED', 'CANCELLED', 'NO_SHOW'].map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + Book vet visit
        </button>
      </div>
      {!isVet && !isAgriOfficer ? <RoleNotice roles={['VET', 'AGRI_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No vet bookings match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Booking Ref</th>
                <th className="table-head">Visit type</th>
                <th className="table-head">Scheduled</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((b) => (
                <tr key={b.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(b.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{b.bookingRef}</td>
                  <td className="table-cell text-xs">{b.visitType.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(b.scheduledAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={b.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <BookingDetailModal id={selectedId} isVet={isVet} isAgriOfficer={isAgriOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <NewBookingModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function BookingDetailModal({
  id,
  isVet,
  isAgriOfficer,
  onClose,
  onChanged,
}: {
  id: string;
  isVet: boolean;
  isAgriOfficer: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const bq = useApiQuery<VetBooking>(`/api/v1/m33/vet-bookings/${id}`);
  const b = bq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diagnosisNotes, setDiagnosisNotes] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      bq.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const complete = () => call(() => api.post(`/api/v1/m33/vet-bookings/${id}/complete`, { diagnosisNotes: diagnosisNotes || undefined }));
  const noShow = () => call(() => api.post(`/api/v1/m33/vet-bookings/${id}/no-show`));
  const cancel = () => call(() => api.post(`/api/v1/m33/vet-bookings/${id}/cancel`, { reason: cancelReason })).then(() => setShowCancel(false));

  return (
    <Modal title={b ? b.bookingRef : 'Vet booking'} onClose={onClose}>
      {bq.error ? <ErrorNote message={bq.error} /> : null}
      {bq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {b ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Detail label="Status" value={<StatusBadge status={b.status} />} />
            <Detail label="Visit type" value={b.visitType.replaceAll('_', ' ')} />
            <Detail label="Requester" value={`${b.requesterType}:${b.requesterId}`} mono />
            <Detail label="Scheduled" value={formatDateTime(b.scheduledAt)} />
            <Detail label="Clinic location" value={b.clinicLocation ?? '—'} />
            <Detail label="Holding" value={b.holdingId ?? '—'} mono />
          </div>
          {b.diagnosisNotes ? <Detail label="Diagnosis notes" value={b.diagnosisNotes} /> : null}
          {b.cancelReason ? <ErrorNote message={`Cancelled: ${b.cancelReason}`} /> : null}

          {b.status === 'BOOKED' ? (
            <div className="border-t border-slate-100 pt-4">
              {!showCancel ? (
                <div className="flex flex-wrap gap-2">
                  {isVet ? (
                    <>
                      <div className="flex flex-1 items-center gap-2">
                        <input className="field-input" placeholder="Diagnosis notes (optional)" value={diagnosisNotes} onChange={(e) => setDiagnosisNotes(e.target.value)} />
                        <button className="btn-success" disabled={busy} onClick={complete}>
                          Complete
                        </button>
                      </div>
                      <button className="btn-secondary" disabled={busy} onClick={noShow}>
                        No-show
                      </button>
                    </>
                  ) : null}
                  {isVet || isAgriOfficer ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowCancel(true)}>
                      Cancel
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Cancellation reason">
                    <textarea className="field-input" rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowCancel(false)}>
                      Back
                    </button>
                    <button className="btn-danger" disabled={busy || cancelReason.trim().length < 3} onClick={cancel}>
                      Confirm cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewBookingModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [requesterType, setRequesterType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [requesterId, setRequesterId] = useState('');
  const [visitType, setVisitType] = useState<'CLINIC' | 'FARM_VISIT'>('CLINIC');
  const [holding, setHolding] = useState<LivestockHolding | null>(null);
  const [clinicLocation, setClinicLocation] = useState('Union Vale Veterinary Clinic');
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const path = useMemo(() => (q.length >= 2 ? `/api/v1/m33/livestock-holdings?limit=8&q=${encodeURIComponent(q)}` : null), [q]);
  const results = useApiQuery<Page<LivestockHolding>>(path);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/vet-bookings', {
        requesterType,
        requesterId,
        visitType,
        holdingId: visitType === 'FARM_VISIT' ? holding?.id : undefined,
        clinicLocation: visitType === 'CLINIC' ? clinicLocation : undefined,
        scheduledAt: new Date(scheduledAt).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Book vet visit" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Requester type">
            <select className="field-input" value={requesterType} onChange={(e) => setRequesterType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="PERSON">Person</option>
              <option value="ENTITY">Entity</option>
            </select>
          </Field>
          <Field label={requesterType === 'PERSON' ? 'NIN' : 'Business ID'}>
            <input className="field-input" value={requesterId} onChange={(e) => setRequesterId(e.target.value)} />
          </Field>
        </div>
        <Field label="Visit type">
          <select className="field-input" value={visitType} onChange={(e) => setVisitType(e.target.value as 'CLINIC' | 'FARM_VISIT')}>
            <option value="CLINIC">Clinic</option>
            <option value="FARM_VISIT">Farm visit</option>
          </select>
        </Field>
        {visitType === 'CLINIC' ? (
          <Field label="Clinic location">
            <input className="field-input" value={clinicLocation} onChange={(e) => setClinicLocation(e.target.value)} />
          </Field>
        ) : (
          <Field label="Holding">
            {holding ? (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                <span>
                  {holding.holdingNo} — {holding.farmName}
                </span>
                <button className="text-xs font-semibold text-brand-700 underline" onClick={() => setHolding(null)}>
                  Change
                </button>
              </div>
            ) : (
              <div>
                <input className="field-input" placeholder="Search holding…" value={q} onChange={(e) => setQ(e.target.value)} />
                {results.data && results.data.items.length > 0 ? (
                  <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-slate-200">
                    {results.data.items.map((h) => (
                      <button key={h.id} className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50" onClick={() => setHolding(h)}>
                        {h.holdingNo} — {h.farmName}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </Field>
        )}
        <Field label="Scheduled at">
          <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </Field>
        <button
          className="btn-primary w-full"
          disabled={busy || !requesterId || !scheduledAt || (visitType === 'FARM_VISIT' && !holding) || (visitType === 'CLINIC' && !clinicLocation)}
          onClick={submit}
        >
          {busy ? 'Booking…' : 'Book visit'}
        </button>
      </div>
    </Modal>
  );
}
